import { InsightPattern } from '../features/insights/types';
import {
  AuthorizedState,
  CosmosResponse,
  MindmapData,
  NounCount,
  ReportMetadata,
  SageThought,
  SageThoughts,
  Thought,
  User,
  UserResponse,
  WellnessCalendarData,
  WellnessScoreData,
} from './schema';

type UUID = string; // Define UUID as a string alias

// Authentication types
export const SET_SESSION_TOKEN = 'SET_SESSION_TOKEN';
export const SET_PIN_TOKEN = 'SET_PIN_TOKEN';
export const SET_AUTHORIZED = 'SET_AUTHORIZED';
export const SET_AUTHENTICATED = 'SET_AUTHENTICATED';
export const SET_AUTHORIZED_STATE = 'SET_AUTHORIZED_STATE';
export const USER_LOGOUT = 'USER_LOGOUT';

export const set_authorized_state = (state: AuthorizedState) => ({
  type: SET_AUTHORIZED_STATE,
  payload: { state },
});
export const setAuthorized = (isAuthorized: boolean) => ({
  type: SET_AUTHORIZED,
  payload: { isAuthorized },
});

export const setAuthenticated = (isAuthenticated: boolean) => ({
  type: SET_AUTHENTICATED,
  payload: { isAuthenticated },
});

export const setSessionToken = (token: string) => ({
  type: SET_SESSION_TOKEN,
  payload: { token },
});

export const setPinToken = (token: string) => ({
  type: SET_PIN_TOKEN,
  payload: { token },
});

// Pulse of Planet Types
export const ADD_BREATH_COUNT = 'ADD_BREATH_COUNT';
export const update_breath_count = (count: number) => ({
  type: ADD_BREATH_COUNT,
  payload: { count },
});
export const SET_BREATH_COUNT = 'SET_BREATH_COUNT';
export const set_breath_count = (count: number) => ({
  type: SET_BREATH_COUNT,
  payload: { count },
});

export const ADD_GRATITUDE = 'ADD_GRATITUDE';
export const add_gratitude = (text: string) => ({
  type: ADD_GRATITUDE,
  payload: { text },
});
export const SET_GRATITUDES = 'SET_GRATITUDES';
export const set_gratitudes = (gratitudes: string[]) => ({
  type: SET_GRATITUDES,
  payload: { gratitudes },
});

// Stats Types
export const FETCH_THOUGHT_NOUNS = 'FETCH_THOUGHT_NOUNS';
export const FETCH_SENTIMENT_SCORE = 'FETCH_SENTIMENT_SCORE';
export const GET_WEEKLY_GOLDEN_HOUR_STATS = 'GET_WEEKLY_GOLDEN_HOUR_STATS';
export const GET_MONTHLY_GOLDEN_HOUR_STATS = 'GET_MONTHLY_GOLDEN_HOUR_STATS';
export const GET_THOUGHT_COUNTS = 'GET_THOUGHT_COUNTS';
export const GET_THOUGHT_NOUNS = 'GET_THOUGHT_NOUNS';

export const fetchThoughtNouns = (nouns: NounCount) => ({
  type: FETCH_THOUGHT_NOUNS as typeof FETCH_THOUGHT_NOUNS,
  payload: { nouns },
});

export const fetchSentimentScore = (score: number) => ({
  type: FETCH_SENTIMENT_SCORE as typeof FETCH_SENTIMENT_SCORE,
  payload: { score },
});

export const getWeeklyGoldenHourStats = (weeklyGoldenHour: number[][]) => ({
  type: GET_WEEKLY_GOLDEN_HOUR_STATS as typeof GET_WEEKLY_GOLDEN_HOUR_STATS,
  payload: { weeklyGoldenHour },
});

export const getMonthlyGoldenHourStats = (monthlyGoldenHour: number[][]) => ({
  type: GET_MONTHLY_GOLDEN_HOUR_STATS as typeof GET_MONTHLY_GOLDEN_HOUR_STATS,
  payload: { monthlyGoldenHour },
});

export const getThoughtNouns = (
  type: string,
  positiveThoughtNouns: NounCount,
  neutralThoughtNouns: NounCount,
  negativeThoughtNouns: NounCount,
) => ({
  type: GET_THOUGHT_NOUNS as typeof GET_THOUGHT_NOUNS,
  payload: {
    type,
    positiveThoughtNouns,
    neutralThoughtNouns,
    negativeThoughtNouns,
  },
});

export const getThoughtCounts = (
  type: string,
  numPositiveThoughts: number,
  numNeutralThoughts: number,
  numNegativeThoughts: number,
) => ({
  type: GET_THOUGHT_COUNTS as typeof GET_THOUGHT_COUNTS,
  payload: {
    type,
    numPositiveThoughts,
    numNeutralThoughts,
    numNegativeThoughts,
  },
});

// Config Types
export const MODIFY_TIMER_DURATION = 'MODIFY_TIMER_DURATION';
export const modifyTimerDuration = (duration: number) => ({
  type: MODIFY_TIMER_DURATION,
  payload: { duration },
});

//Thought Types
export const ADD_PLACEHOLDER_THOUGHT = 'ADD_PLACEHOLDER_THOUGHT';
export const ADD_THOUGHT_SUCCESS = 'ADD_THOUGHT_SUCCESS';
export const ADD_PENDING_THOUGHT = 'ADD_PENDING_THOUGHT';
export const RESOLVE_PENDING_THOUGHT = 'RESOLVE_PENDING_THOUGHT';
export const FAIL_PENDING_THOUGHT = 'FAIL_PENDING_THOUGHT';
export const UPDATE_SAGE_THOUGHT = 'UPDATE_SAGE_THOUGHT';
export const SELECT_THOUGHT_EMOTIONS = 'SELECT_THOUGHT_EMOTIONS';
export const REMOVE_THOUGHT = 'REMOVE_THOUGHT';
export const REMOVE_THOUGHT_EMOTIONS = 'REMOVE_THOUGHT_EMOTIONS';
export const DELETE_THOUGHT_START = 'DELETE_THOUGHT_START';
export const DELETE_THOUGHT_SUCCESS = 'DELETE_THOUGHT_SUCCESS';
export const DELETE_THOUGHT_FAILURE = 'DELETE_THOUGHT_FAILURE';
export const SET_CURRENT_MONTH_THOUGHTS = 'SET_CURRENT_MONTH_THOUGHTS';
export const SET_THOUGHTS_LOADING = 'SET_THOUGHTS_LOADING';
export const UPDATE_THOUGHT_AGENT_RESPONSE = 'UPDATE_THOUGHT_AGENT_RESPONSE';

export const addPlaceholderThought = (thought: Thought) => ({
  type: ADD_PLACEHOLDER_THOUGHT,
  payload: { thought },
});

export const addThoughtSuccess = (thought: Thought) => ({
  type: ADD_THOUGHT_SUCCESS,
  payload: { thought },
});

export const addPendingThought = (
  pendingId: string,
  message: string,
  tags: string[],
  journal_prompt?: string,
  parent_id?: string,
) => ({
  type: ADD_PENDING_THOUGHT,
  payload: { pendingId, message, tags, journal_prompt, parent_id },
});

export const resolvePendingThought = (pendingId: string, thought: Thought) => ({
  type: RESOLVE_PENDING_THOUGHT,
  payload: { pendingId, thought },
});

export const failPendingThought = (pendingId: string, error: string) => ({
  type: FAIL_PENDING_THOUGHT,
  payload: { pendingId, error },
});

export const update_sage_thought = (thought_uuid: UUID, reframed_thought: string) => ({
  type: UPDATE_SAGE_THOUGHT,
  payload: { thought_uuid, reframed_thought },
});

export const selectThoughtEmotions = (uuid: UUID, user_selected_emotions: string[]) => ({
  type: SELECT_THOUGHT_EMOTIONS,
  payload: { uuid, user_selected_emotions },
});

export const removeThoughtEmotions = (uuid: UUID) => ({
  type: REMOVE_THOUGHT_EMOTIONS,
  payload: { uuid },
});

export const removeThought = () => ({
  type: REMOVE_THOUGHT,
  payload: {},
});

export const deleteThoughtStart = (uuid: UUID, thought: Thought) => ({
  type: DELETE_THOUGHT_START,
  payload: { uuid, thought },
});

export const deleteThoughtSuccess = (uuid: UUID) => ({
  type: DELETE_THOUGHT_SUCCESS,
  payload: { uuid },
});

export const deleteThoughtFailure = (uuid: UUID, thought: Thought, error: string) => ({
  type: DELETE_THOUGHT_FAILURE,
  payload: { uuid, thought, error },
});

export const updateThoughtAgentResponse = (
  uuid: UUID,
  agentResponse?: string,
  guiding_questions?: string[],
  error?: { type: string; statusCode: number },
) => ({
  type: UPDATE_THOUGHT_AGENT_RESPONSE,
  payload: {
    uuid,
    agentResponse,
    guiding_questions,
    error: error ? JSON.stringify(error) : undefined,
  },
});

export const set_all_thoughts = (thoughts: Thought[]) => ({
  type: SET_CURRENT_MONTH_THOUGHTS,
  payload: { thoughts },
});

export const set_thoughts_loading = (loading: boolean) => ({
  type: SET_THOUGHTS_LOADING,
  payload: { loading },
});

export const set_curent_month_sage_thoughts = (thoughts: Thought[]) => ({
  type: SET_CURRENT_MONTH_THOUGHTS as typeof SET_CURRENT_MONTH_THOUGHTS,
  payload: { thoughts },
});

// Chat action types
export const SET_COSMOS_LATEST_RESPONSE = 'SET_COSMOS_LATEST_RESPONSE';
export const SET_USERS_LATEST_RESPONSE = 'SET_USERS_LATEST_RESPONSE';
export const UPDATE_USERS_LATEST_RESPONSE_FROM_THOUGHT =
  'UPDATE_USERS_LATEST_RESPONSE_FROM_THOUGHT';

// Chat action creators
export const set_cosmos_latest_response = (cosmosResponse: CosmosResponse) => ({
  type: SET_COSMOS_LATEST_RESPONSE,
  payload: { cosmosResponse },
});

export const set_users_latest_response = (userResponse: UserResponse) => ({
  type: SET_USERS_LATEST_RESPONSE,
  payload: { userResponse },
});

export const update_users_latest_response_from_thought = () => ({
  type: UPDATE_USERS_LATEST_RESPONSE_FROM_THOUGHT,
  payload: {},
});

// export const get_thoughts_middleware = (thoughts: Thought[]) => ({

// })
export const CREATE_SAGE_THOUGHT = 'CREATE_SAGE_THOUGHT';
export const create_sage_thought_middleware = (thought_uuid: UUID, sage_thought: SageThought) => ({
  type: CREATE_SAGE_THOUGHT,
  payload: { thought_uuid: thought_uuid, sage_thought: sage_thought },
});

export const SET_SAGE_THOUGHTS = 'SET_SAGE_THOUGHTS';
export const set_sage_thoughts = (sage_thoughts: SageThoughts) => ({
  type: SET_SAGE_THOUGHTS,
  payload: { sage_thoughts: sage_thoughts },
});

// User Types
export const FETCH_USER = 'FETCH_USER';
export const fetchUser = (user: User) => ({
  type: FETCH_USER,
  payload: { user },
});

export const userLogout = () => ({
  type: USER_LOGOUT,
});

export const SET_REPORT_METADATA = 'SET_REPORT_METADATA';

export const set_report_metadata = (metadata: ReportMetadata) => ({
  type: SET_REPORT_METADATA,
  payload: { metadata },
});

// Mindmap Types
export const SET_MINDMAP = 'SET_MINDMAP';
export const SET_MINDMAP_LOADING = 'SET_MINDMAP_LOADING';
export const SET_MINDMAP_ERROR = 'SET_MINDMAP_ERROR';

export const set_mindmap = (data: MindmapData) => ({
  type: SET_MINDMAP,
  payload: { data },
});

export const set_mindmap_loading = (loading: boolean) => ({
  type: SET_MINDMAP_LOADING,
  payload: { loading },
});

export const set_mindmap_error = (error: string) => ({
  type: SET_MINDMAP_ERROR,
  payload: { error },
});

// Wellness Score Types
export const SET_WELLNESS_SCORE = 'SET_WELLNESS_SCORE';
export const SET_WELLNESS_SCORE_LOADING = 'SET_WELLNESS_SCORE_LOADING';
export const SET_WELLNESS_SCORE_ERROR = 'SET_WELLNESS_SCORE_ERROR';

export const set_wellness_score = (data: WellnessScoreData) => ({
  type: SET_WELLNESS_SCORE,
  payload: { data },
});

export const set_wellness_score_loading = (loading: boolean) => ({
  type: SET_WELLNESS_SCORE_LOADING,
  payload: { loading },
});

export const set_wellness_score_error = (error: string) => ({
  type: SET_WELLNESS_SCORE_ERROR,
  payload: { error },
});

// Wellness Calendar Types
export const SET_WELLNESS_CALENDAR = 'SET_WELLNESS_CALENDAR';
export const SET_WELLNESS_CALENDAR_LOADING = 'SET_WELLNESS_CALENDAR_LOADING';
export const SET_WELLNESS_CALENDAR_ERROR = 'SET_WELLNESS_CALENDAR_ERROR';

export const set_wellness_calendar = (data: WellnessCalendarData) => ({
  type: SET_WELLNESS_CALENDAR,
  payload: { data },
});

export const set_wellness_calendar_loading = (loading: boolean) => ({
  type: SET_WELLNESS_CALENDAR_LOADING,
  payload: { loading },
});

export const set_wellness_calendar_error = (error: string) => ({
  type: SET_WELLNESS_CALENDAR_ERROR,
  payload: { error },
});

// Insights Types
export const SET_INSIGHTS_PATTERNS = 'SET_INSIGHTS_PATTERNS';
export const SET_INSIGHTS_LOADING = 'SET_INSIGHTS_LOADING';
export const SET_INSIGHTS_ERROR = 'SET_INSIGHTS_ERROR';

export const set_insights_patterns = (patterns: InsightPattern[]) => ({
  type: SET_INSIGHTS_PATTERNS,
  payload: { patterns },
});

export const set_insights_loading = (loading: boolean) => ({
  type: SET_INSIGHTS_LOADING,
  payload: { loading },
});

export const set_insights_error = (error: string) => ({
  type: SET_INSIGHTS_ERROR,
  payload: { error },
});
